import { toast } from "react-toastify"

import { REQ } from "constants/api/requests"
import { IResponse } from "constants/interfaces"

import useApi from "util/api"

import { CreateChallengeInput, GameChallenge, GetAllChallengesParams, GetAllChallengesResponse, UpdateChallengeInput } from "./type"

export const getAll = async (params?: GetAllChallengesParams): Promise<GetAllChallengesResponse> => {
  try {
    const { data } = await useApi.get<IResponse<GetAllChallengesResponse>>(REQ.ADMIN_DASHBOARD.CHALLENGES.GET_ALL, { params })
    if (data.success) return data.response ?? { data: [], total: 0 }
    return { data: [], total: 0 }
  } catch (error) {
    toast.error("Failed to load challenges")
    return { data: [], total: 0 }
  }
}

export const getById = async (id: string): Promise<GameChallenge | null> => {
  try {
    const url = REQ.ADMIN_DASHBOARD.CHALLENGES.GET.replace(":id", id)
    const { data } = await useApi.get<IResponse<GameChallenge>>(url)
    if (data.success) return data.response ?? null
    return null
  } catch (error) {
    toast.error("Failed to load challenge")
    return null
  }
}

export const create = async (input: CreateChallengeInput): Promise<GameChallenge | null> => {
  try {
    const { data } = await useApi.post<IResponse<GameChallenge>>(REQ.ADMIN_DASHBOARD.CHALLENGES.CREATE, input)
    if (data.success) {
      toast.success("Challenge created")
      return data.response ?? null
    }
    return null
  } catch (error: any) {
    const message = error?.response?.data?.message || "Failed to create challenge"
    toast.error(message)
    return null
  }
}

export const update = async (id: string, input: UpdateChallengeInput): Promise<GameChallenge | null> => {
  try {
    const url = REQ.ADMIN_DASHBOARD.CHALLENGES.UPDATE.replace(":id", id)
    const { data } = await useApi.put<IResponse<GameChallenge>>(url, input)
    if (data.success) {
      toast.success("Challenge updated")
      return data.response ?? null
    }
    return null
  } catch (error: any) {
    const message = error?.response?.data?.message || "Failed to update challenge"
    toast.error(message)
    return null
  }
}

export const remove = async (id: string): Promise<boolean> => {
  try {
    const url = REQ.ADMIN_DASHBOARD.CHALLENGES.DELETE.replace(":id", id)
    const { data } = await useApi.delete<IResponse<GameChallenge>>(url)
    if (data.success) {
      toast.success("Challenge deleted")
      return true
    }
    return false
  } catch (error) {
    toast.error("Failed to delete challenge")
    return false
  }
}

export const activate = async (id: string): Promise<GameChallenge | null> => {
  try {
    const url = REQ.ADMIN_DASHBOARD.CHALLENGES.ACTIVATE.replace(":id", id)
    const { data } = await useApi.put<IResponse<GameChallenge>>(url)
    if (data.success) {
      toast.success("Challenge activated")
      return data.response ?? null
    }
    return null
  } catch (error) {
    toast.error("Failed to activate challenge")
    return null
  }
}

export const deactivate = async (id: string): Promise<GameChallenge | null> => {
  try {
    const url = REQ.ADMIN_DASHBOARD.CHALLENGES.DEACTIVATE.replace(":id", id)
    const { data } = await useApi.put<IResponse<GameChallenge>>(url)
    if (data.success) {
      toast.success("Challenge deactivated")
      return data.response ?? null
    }
    return null
  } catch (error) {
    toast.error("Failed to deactivate challenge")
    return null
  }
}

export const challengeService = {
  getAll,
  getById,
  create,
  update,
  remove,
  activate,
  deactivate
}
